import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const SnagApi = createApi({
  reducerPath: "Snag",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND_URL}`,
    credentials: "include",
  }),
  endpoints: (builder) => ({
    // ==================== GET ENDPOINTS ====================
    getAllSnags: builder.query({
      query: (val) => {
        const page = val.page ?? 1;
        const limit = val.limit ?? 10;
        const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
        const order = val.order ? `&order=${val.order}` : "";
        const search = val.search ? `&search=${val.search}` : "";
        const status = val.status ? `&status=${val.status}` : "";
        const queryString = val.queryString ? `&${val.queryString}` : "";
        return `/snaglist?page=${page}&limit=${limit}${search}${sortBy}${order}${status}${queryString}`;
      },
    }),

    getSpecificSnag: builder.query({
      query: (id) => `/snaglist/${id}`,
    }),

    getSectors: builder.query({
      query: () => "/snagList/sectors",
    }),

    getSubsystems: builder.query({
      query: (sector) => `/snagList/subsystems/${encodeURIComponent(sector)}`,
    }),

    getDisciplines: builder.query({
      query: (subsystem) =>
        `/snagList/disciplines/${encodeURIComponent(subsystem)}`,
    }),

    getItems: builder.query({
      query: (discipline) =>
        `/snagList/items/${encodeURIComponent(discipline)}`,
    }),

    getAcceptanceCriteria: builder.query({
      query: (item) =>
        `/snagList/acceptance-criteria/${encodeURIComponent(item)}`,
    }),

    getInspectionMethods: builder.query({
      query: (acceptanceCriteria) =>
        `/snagList/inspection-methods/${encodeURIComponent(
          acceptanceCriteria,
        )}`,
    }),

    getContractors: builder.query({
      query: (projectId) => `/snagList/contractors/${projectId}`,
    }),

    getOptions: builder.query({
      query: () => `/snagList/options`,
    }),

    getLevelMap: builder.query({
      query: ({ project_id, zone_id, building_id, level }) => {
        const params = new URLSearchParams();
        if (project_id) params.append("project_id", project_id);
        if (zone_id) params.append("zone_id", zone_id);
        if (building_id) params.append("building_id", building_id);
        if (level) params.append("level", level);
        return `/snagList/level-map?${params.toString()}`;
      },
    }),

    getFloorplan: builder.query({
      queryFn: async (poiId, _queryApi, _extraOptions, baseQuery) => {
        const url = `https://fom.softsprint.ae/backendTest/floorplans/${poiId}`;
        const token = localStorage.getItem("token");

        const headers = new Headers();
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }

        try {
          const response = await fetch(url, {
            headers: headers,
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: "FETCH_ERROR", error: error.message } };
        }
      },
    }),

    getAvailableTeams: builder.query({
      query: ({ projectId, zoneId, discipline }) => {
        const params = new URLSearchParams();
        if (projectId) params.append("projectId", projectId);
        if (zoneId) params.append("zoneId", zoneId);
        if (discipline) params.append("discipline", discipline);
        return `/snagList/available-teams?${params.toString()}`;
      },
    }),

    getSnagReport: builder.query({
      queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
        const {
          projectId,
          zoneId,
          levelId,
          roomId,
          parentId,
          status,
          poiTypes,
          startDate,
          endDate,
        } = params;
        const url = new URL(
          "https://fom.softsprint.ae/MaintBackend/api/v1/floorplans/snagReport",
        );

        if (projectId) url.searchParams.append("projectId", projectId);
        if (zoneId) url.searchParams.append("zoneId", zoneId);
        if (levelId) url.searchParams.append("levelId", levelId);
        if (roomId) url.searchParams.append("roomId", roomId); // Changed from spaceId to roomId
        if (parentId) url.searchParams.append("parentId", parentId); // Add parentId support
        if (status) url.searchParams.append("status", status);
        if (poiTypes) url.searchParams.append("poiTypes", poiTypes);
        if (startDate) url.searchParams.append("startDate", startDate);
        if (endDate) url.searchParams.append("endDate", endDate);

        const token = localStorage.getItem("token");
        const headers = new Headers();
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }

        console.log("🔍 Fetching snag report:", url.toString()); // Debug log

        try {
          const response = await fetch(url.toString(), {
            headers: headers,
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          console.log("✅ Snag report data:", data); // Debug log
          return { data };
        } catch (error) {
          console.error("❌ Snag report error:", error); // Debug log
          return { error: { status: "FETCH_ERROR", error: error.message } };
        }
      },
      // Don't cache - always fetch fresh data
      keepUnusedDataFor: 0,
    }),

    // ==================== EXPORT ENDPOINTS ====================

    // ✅ Export to Excel - FIXED FILENAME EXTRACTION
    exportSnagListToExcel: builder.mutation({
      queryFn: async (filters, _queryApi, _extraOptions, fetchWithBQ) => {
        try {
          const params = new URLSearchParams();
          if (filters.search) params.append("search", filters.search);
          if (filters.status) params.append("status", filters.status);
          if (filters.sortby) params.append("sortBy", filters.sortby);
          if (filters.order) params.append("order", filters.order);

          const token = localStorage.getItem("token");
          const url = `${
            process.env.REACT_APP_BACKEND_URL
          }/snaglist/export/excel?${params.toString()}`;

          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`Export failed: ${response.statusText}`);
          }

          // ✅ FIXED: Better filename extraction
          const contentDisposition = response.headers.get(
            "Content-Disposition",
          );
          let filename = "snag-list.xlsx"; // Default fallback

          if (contentDisposition) {
            // Try to match: filename="something.xlsx" or filename=something.xlsx
            const filenameMatch = contentDisposition.match(
              /filename[^;=\n]*=["']?([^"'\n;]+)["']?/i,
            );
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].trim();
              // Remove any trailing quotes or underscores
              filename = filename
                .replace(/^["']|["']$/g, "")
                .replace(/_+$/, "");
            }
          }

          // ✅ Final safety check - ensure proper extension
          if (!filename.endsWith(".xlsx")) {
            filename = filename.replace(/\.(xlsx|xls)?.*$/, ".xlsx");
          }

          console.log("📥 Downloading Excel file:", filename); // Debug log

          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);

          return { data: { success: true, filename } };
        } catch (error) {
          return { error: { status: "FETCH_ERROR", error: error.message } };
        }
      },
    }),

    // ✅ Export to PDF - FIXED FILENAME EXTRACTION
    exportSnagListToPdf: builder.mutation({
      queryFn: async (filters, _queryApi, _extraOptions, fetchWithBQ) => {
        try {
          const params = new URLSearchParams();
          if (filters.search) params.append("search", filters.search);
          if (filters.status) params.append("status", filters.status);
          if (filters.sortby) params.append("sortBy", filters.sortby);
          if (filters.order) params.append("order", filters.order);

          const token = localStorage.getItem("token");
          const url = `${
            process.env.REACT_APP_BACKEND_URL
          }/snaglist/export/pdf?${params.toString()}`;

          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`Export failed: ${response.statusText}`);
          }

          // ✅ FIXED: Better filename extraction
          const contentDisposition = response.headers.get(
            "Content-Disposition",
          );
          let filename = "snag-list.pdf"; // Default fallback

          if (contentDisposition) {
            // Try to match: filename="something.pdf" or filename=something.pdf
            const filenameMatch = contentDisposition.match(
              /filename[^;=\n]*=["']?([^"'\n;]+)["']?/i,
            );
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].trim();
              // Remove any trailing quotes or underscores
              filename = filename
                .replace(/^["']|["']$/g, "")
                .replace(/_+$/, "");
            }
          }

          // ✅ Final safety check - ensure proper extension
          if (!filename.endsWith(".pdf")) {
            filename = filename.replace(/\.(pdf)?.*$/, ".pdf");
          }

          console.log("📥 Downloading PDF file:", filename); // Debug log

          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);

          return { data: { success: true, filename } };
        } catch (error) {
          return { error: { status: "FETCH_ERROR", error: error.message } };
        }
      },
    }),

    // ==================== CREATE ENDPOINTS ====================
    bulkCreateSnags: builder.mutation({
      query: (formData) => ({
        url: "/snaglist/bulk",
        method: "POST",
        body: formData,
      }),
    }),

    // ==================== UPDATE ENDPOINTS ====================

    // ✅ NEW: Edit/Update Snag
    updateSnag: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/snaglist/${id}`,
        method: "PUT",
        body: formData,
      }),
    }),

    // ==================== STATUS UPDATE ENDPOINTS ====================

    fixSnag: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/snaglist/${id}/fix`,
        method: "POST",
        body: formData,
      }),
    }),

    rectifySnag: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/snaglist/${id}/rectify`,
        method: "POST",
        body: formData,
      }),
    }),

    verifySnag: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/snaglist/${id}/qa-verify`,
        method: "POST",
        body: formData,
      }),
    }),

    closeSnag: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/snaglist/${id}/close`,
        method: "POST",
        body: formData,
      }),
    }),

    reopenSnag: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/snaglist/${id}/reopen`,
        method: "POST",
        body: formData,
      }),
    }),

    disputeSnag: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/snaglist/${id}/dispute`,
        method: "POST",
        body: formData,
      }),
    }),

    // ==================== LEGACY ENDPOINTS ====================
    qaReviewSnag: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/snaglist/${id}/qa-verify`,
        method: "POST",
        body: payload,
      }),
    }),

    consultantReviewSnag: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/snaglist/${id}/consultant-review`,
        method: "POST",
        body: payload,
      }),
    }),

    // ==================== DELETE ENDPOINT ====================
    deleteSnag: builder.mutation({
      query: (id) => ({
        url: `/snaglist/${id}`,
        method: "DELETE",
      }),
    }),
    snagFilterValues: builder.query({
      query: () => ({
        url: "/snaglist/filter-options",
        method: "GET",
      }),
    }),
  }),
});

export const {
  // GET hooks
  useGetAllSnagsQuery,
  useGetSpecificSnagQuery,
  useGetSectorsQuery,
  useGetSubsystemsQuery,
  useGetDisciplinesQuery,
  useGetItemsQuery,
  useGetAcceptanceCriteriaQuery,
  useGetInspectionMethodsQuery,
  useGetContractorsQuery,
  useGetOptionsQuery,
  useGetLevelMapQuery,
  useGetFloorplanQuery,
  useGetSnagReportQuery,

  // EXPORT hooks
  useExportSnagListToExcelMutation,
  useExportSnagListToPdfMutation,

  // CREATE hooks
  useBulkCreateSnagsMutation,

  // UPDATE hooks
  useUpdateSnagMutation, // ✅ NEW
  useGetAvailableTeamsQuery,

  // STATUS UPDATE hooks
  useFixSnagMutation,
  useRectifySnagMutation,
  useVerifySnagMutation,
  useCloseSnagMutation,
  useReopenSnagMutation,
  useDisputeSnagMutation,

  // LEGACY hooks
  useQaReviewSnagMutation,
  useConsultantReviewSnagMutation,

  // DELETE hook
  useDeleteSnagMutation,

  // FILTER hooks
  useSnagFilterValuesQuery,
} = SnagApi;
